<template>
  <v-card outlined class="rounded-lg">
    <v-card-text>
      <!-- Header -->
      <div class="d-flex align-start mb-4">
        <v-avatar size="48" color="primary" class="mr-3 mt-1" rounded>
          <v-icon color="white">mdi-trending-up</v-icon>
        </v-avatar>
        <div class="flex-grow-1">
          <div class="text-h6 font-weight-bold">Growth Score</div>
          <div class="body-2 grey--text">Weighted across<br>KPI, tasks,<br>AI &amp; habits</div>
        </div>
        <div class="text-right ml-2">
          <div class="caption grey--text text-uppercase font-weight-medium">Level</div>
          <div class="subtitle-1 font-weight-black">{{ levelName(totalPoints) }}</div>
          <div class="caption grey--text" style="font-size:10px;line-height:1.4;">
            KPI×40% +<br>Tasks×25% +<br>AI×20% +<br>Habit×15%
          </div>
        </div>
      </div>

      <!-- Score gauge + stats -->
      <div class="d-flex align-center mb-4" style="gap:16px;">
        <!-- Circular gauge -->
        <div style="position:relative;width:120px;height:120px;flex-shrink:0;">
          <svg width="120" height="120" viewBox="0 0 100 100" style="transform:rotate(-90deg)">
            <circle cx="50" cy="50" r="42" fill="none" stroke="#e0e0e0" stroke-width="9" />
            <circle cx="50" cy="50" r="42" fill="none" stroke="#1976d2" stroke-width="9" stroke-linecap="round"
      :stroke-dasharray="`${Math.min(growthScore * 2.64, 264)} ${264 - Math.min(growthScore * 2.64, 264)}`" />
          </svg>
          <div style="position:absolute;inset:0;display:flex;flex-direction:column;align-items:center;justify-content:center;">
            <span class="text-h5 font-weight-black primary--text">{{ growthScore }}</span>
            <span class="caption grey--text">/ 100</span>
          </div>
        </div>

        <!-- Today + Total -->
        <div class="flex-grow-1 d-flex flex-column" style="gap:8px;">
          <div class="pa-3 rounded-lg d-flex align-center justify-space-between" style="background:#f5f5f5;border:1px solid #e0e0e0;">
            <div class="d-flex align-center">
              <v-icon small color="primary" class="mr-2">mdi-creation</v-icon>
              <span class="body-2 grey--text">Today</span>
            </div>
            <div class="text-right">
              <span class="text-h6 font-weight-black">{{ todayPoints }}</span>
              <span class="caption grey--text ml-1">pts</span>
            </div>
          </div>
          <div class="pa-3 rounded-lg d-flex align-center justify-space-between" style="background:#f5f5f5;border:1px solid #e0e0e0;">
            <div class="d-flex align-center">
              <v-icon small color="amber darken-1" class="mr-2">mdi-trophy</v-icon>
              <span class="body-2 grey--text">Total</span>
            </div>
            <div class="text-right">
              <span class="text-h6 font-weight-black">{{ totalPoints }}</span>
              <span class="caption grey--text ml-1">pts</span>
            </div>
          </div>
        </div>
      </div>

      <v-divider class="mb-4" />

      <!-- Daily Leaderboard -->
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <v-icon small color="amber darken-1" class="mr-2">mdi-trophy</v-icon>
          <span class="subtitle-2 font-weight-black text-uppercase tracking-wide">Daily Leaderboard</span>
        </div>
        <span class="caption grey--text">Top performers today</span>
      </div>

      <div v-for="(entry, i) in leaderboard.slice(0, 5)" :key="entry.name"
        class="d-flex align-center pa-3 rounded-lg mb-2"
        :style="entry.isYou
          ? 'background:#fff8e1;border:1px solid #ffe082;'
          : 'background:#f5f5f5;border:1px solid #e0e0e0;'"
      >
        <!-- Rank badge -->
        <div class="mr-3 d-flex align-center justify-center"
          style="width:32px;height:32px;border-radius:50%;flex-shrink:0;"
          :style="i === 0 ? 'background:#fff8e1;' : i === 1 ? 'background:#f5f5f5;' : i === 2 ? 'background:#fce4ec;' : 'background:#eeeeee;'"
        >
          <v-icon v-if="i === 0" small color="amber darken-2">mdi-crown</v-icon>
          <v-icon v-else-if="i === 1" small color="grey">mdi-medal</v-icon>
          <v-icon v-else-if="i === 2" small color="pink lighten-2">mdi-medal-outline</v-icon>
          <span v-else class="caption grey--text font-weight-bold">{{ i + 1 }}</span>
        </div>

        <span class="body-2 flex-grow-1" :class="entry.isYou ? 'font-weight-black' : 'font-weight-medium'" style="cursor:pointer;" @click.stop="openProfile(entry)">
          {{ entry.name }}
        </span>

        <div class="text-right">
          <span class="subtitle-2 font-weight-black" :class="entry.isYou ? 'primary--text' : ''">
            {{ entry.score }}
          </span>
          <span class="caption grey--text ml-1">pts</span>
        </div>
      </div>

      <!-- View full ranking button -->
      <v-btn block text color="primary" class="text-none mt-2 rounded-lg" style="border:1px solid #e3f2fd;" @click="showDailyModal = true">
        View full ranking ({{ leaderboard.length }})
        <v-icon small right>mdi-chevron-right</v-icon>
      </v-btn>

    </v-card-text>

    <!-- Daily Full Ranking Modal -->
    <v-dialog v-model="showDailyModal" max-width="480" scrollable>
      <v-card class="rounded-xl">
        <v-card-title class="d-flex align-center justify-space-between pb-2">
          <div class="d-flex align-center">
            <v-icon color="amber darken-1" class="mr-2">mdi-trophy</v-icon>
            <span class="subtitle-1 font-weight-black">Daily Leaderboard</span>
          </div>
          <v-btn icon small @click="showDailyModal = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>
        <v-divider />
        <v-card-text class="pa-3" style="max-height:70vh;overflow-y:auto;">
          <div v-for="(entry, i) in leaderboard" :key="entry.name"
            class="d-flex align-center pa-3 rounded-lg mb-2"
            :style="entry.isYou
              ? 'background:#fff8e1;border:1px solid #ffe082;'
              : 'background:#f5f5f5;border:1px solid #e0e0e0;'"
          >
            <div class="mr-3 d-flex align-center justify-center"
              style="width:32px;height:32px;border-radius:50%;flex-shrink:0;"
              :style="i === 0 ? 'background:#fff8e1;' : i === 1 ? 'background:#f5f5f5;' : i === 2 ? 'background:#fce4ec;' : 'background:#eeeeee;'"
            >
              <v-icon v-if="i === 0" small color="amber darken-2">mdi-crown</v-icon>
              <v-icon v-else-if="i === 1" small color="grey">mdi-medal</v-icon>
              <v-icon v-else-if="i === 2" small color="pink lighten-2">mdi-medal-outline</v-icon>
              <span v-else class="caption grey--text font-weight-bold">{{ i + 1 }}</span>
            </div>
            <span class="body-2 flex-grow-1" :class="entry.isYou ? 'font-weight-black' : 'font-weight-medium'" style="cursor:pointer;" @click.stop="openProfile(entry)">
              {{ entry.name }}
              <v-chip v-if="entry.isYou" x-small color="amber lighten-3" class="ml-1" style="height:16px;">you</v-chip>
            </span>
            <div class="text-right">
              <span class="subtitle-2 font-weight-black" :class="entry.isYou ? 'primary--text' : ''">
                {{ entry.score }}
              </span>
              <span class="caption grey--text ml-1">pts</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- User Profile Dialog -->
    <v-dialog v-model="profileDialog" max-width="420">
      <v-card class="rounded-xl" v-if="profileUser">
        <v-card-text class="pa-6">
          <div class="d-flex align-start mb-4">
            <v-avatar color="primary" size="64" class="white--text text-h5 font-weight-medium mr-4">
              <v-img v-if="profileUser.profile_picture" :src="getProfilePictureUrl(profileUser.profile_picture)" />
              <span v-else>{{ getUserInitials(profileUser) }}</span>
            </v-avatar>
            <div class="flex-grow-1">
              <h3 class="text-h6 font-weight-bold mb-1">{{ profileUser.name }}</h3>
              <p class="text-subtitle-2 text--secondary mb-0 text-uppercase" style="font-size:11px;letter-spacing:0.05em;">
                {{ profileUser.position_name || 'No Position' }}
              </p>
            </div>
            <v-btn icon small @click="profileDialog = false"><v-icon>mdi-close</v-icon></v-btn>
          </div>
          <div v-if="profileUser.skills && profileUser.skills.length" class="mb-3">
            <div class="d-flex flex-wrap">
              <v-chip v-for="(skill, idx) in profileUser.skills.slice(0, 3)" :key="idx" x-small label class="mr-2 mb-2" color="primary" outlined>{{ skill }}</v-chip>
              <v-chip v-if="profileUser.skills.length > 3" x-small label class="mb-2">+{{ profileUser.skills.length - 3 }}</v-chip>
            </div>
          </div>
          <v-divider class="mb-3" />
          <div v-if="profileUser.role_members && profileUser.role_members.filter(m => m.role && ![1,2].includes(m.role.id)).length" class="d-flex align-center text-caption text--secondary">
            <v-icon x-small class="mr-1">mdi-account-group</v-icon>
            <span>Team: <strong>{{ profileUser.role_members.filter(m => m.role && ![1,2].includes(m.role.id)).map(m => m.role.name).join(', ') }}</strong></span>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>

/*
 * ── GROWTH SCORE — SPEC COMPLIANT ───────────────────────────────────────────
 *
 * Growth Score (0–100) = (kpi × 0.40) + (tasks × 0.25) + (ai × 0.20) + (habits × 0.15)
 *
 * All four pillar scores (0–100) come from the backend
 * (api/employee-growth/score) which applies the spec's normalization:
 *   - Task pillar  = taskPtsToday  / 30 * 100   (cap 30 pts/day)
 *   - AI pillar    = aiPtsToday    / 20 * 100   (cap 20 pts/day)
 *   - Habit pillar = habitPtsToday /  5 * 100   (5 pts/day baseline)
 *   - KPI pillar   = kpiPts / 10                (cumulative vs target)
 *
 * The frontend does NOT recompute pillars locally — doing so would drift from
 * the backend and the leaderboards. This card is display-only.
 *
 * LEVEL SYSTEM (by total points):
 *   Starter 0–499 | Performer 500–1499 | Achiever 1500–2999 | Elite 3000–5999 | Top Operator 6000+
 * ────────────────────────────────────────────────────────────────────────────
 */

// Level thresholds — total points based (matches backend spec)
const LEVELS = [
  { name: 'Starter',      min: 0 },
  { name: 'Performer',    min: 500 },
  { name: 'Achiever',     min: 1500 },
  { name: 'Elite',        min: 3000 },
  { name: 'Top Operator', min: 6000 },
];

export default {
  name: 'GrowthScoreCard',
  inject: {
    users: { default: () => () => [] },
  },
  data() {
    return {
      loading: false,
      showDailyModal: false,
      profileDialog: false,
      profileUser: null,
      snap: { todayPoints: 0, totalPoints: 0 },
      pillarScores: { kpi: 0, tasks: 0, ai: 0, habits: 0 },
      leaderboard: [],
    };
  },
  computed: {
    myUserId() {
      return this.$store.state.auth.user?.id;
    },
    todayPoints() {
      return this.snap.todayPoints;
    },
    totalPoints() {
      return this.snap.totalPoints;
    },
    // Spec formula: KPI×40 + Tasks×25 + AI×20 + Habit×15. All pillars 0–100
    // from backend; weighted on the client for display.
    growthScore() {
      const p = this.pillarScores;
      return Math.round(p.kpi * 0.40 + p.tasks * 0.25 + p.ai * 0.20 + p.habits * 0.15);
    },
  },
  mounted() {
    this.load();
    this.$root.$on('growth:refresh', this.load);
  },
  beforeDestroy() {
    this.$root.$off('growth:refresh', this.load);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [scoreRes, lbRes] = await Promise.all([
          this.$axios.get('api/employee-growth/score'),
          this.$axios.get('api/employee-growth/leaderboard', { period: 'daily', _t: Date.now() }),
        ]);
        if (scoreRes.data.response) {
          const d = scoreRes.data.data;
          // All four pillar scores are spec-normalized on the backend — trust
          // them directly so this card stays consistent with leaderboards and
          // RoleProfileCard. No client-side recomputation.
          this.pillarScores = {
            kpi:    d.kpi_score    ?? d.pillar_scores?.kpi    ?? 0,
            tasks:  d.task_score   ?? d.pillar_scores?.tasks  ?? 0,
            ai:     d.ai_score     ?? d.pillar_scores?.ai     ?? 0,
            habits: d.habit_score  ?? d.pillar_scores?.habits ?? 0,
          };
          // Canonical totals from growth_scores table (matches leaderboard)
          this.snap.todayPoints = d.today_points ?? 0;
          this.snap.totalPoints = d.total_points ?? 0;
        }
        if (lbRes.data.response) {
          this.leaderboard = lbRes.data.data.map(e => ({
            ...e,
            isYou: e.user_id === this.myUserId,
          }));
        }
      } finally {
        this.loading = false;
      }
    },

    // Level is determined by total points, not the 0-100 growth score
    levelName(totalPts) {
      let current = LEVELS[0];
      for (const lvl of LEVELS) {
        if (totalPts >= lvl.min) current = lvl;
      }
      return current.name;
    },
    openProfile(entry) {
      const allUsers = typeof this.users === 'function' ? this.users() : [];
      this.profileUser = allUsers.find(u => u.id === entry.user_id) || null;
      if (this.profileUser) this.profileDialog = true;
    },
    getProfilePictureUrl(path) {
      return (process.env.VUE_APP_API_URL || '') + path;
    },
    getUserInitials(user) {
      if (!user || !user.name) return 'U';
      return user.name.split(' ').map(p => p[0]).join('').toUpperCase().substring(0, 2);
    },
  },
};
</script>
